/**
 * Turns raw diary.csv / watched.csv / ratings.csv text into one merged list
 * of "this film, watched on this date" records — one per film, ready for the
 * matching ladder in match.ts.
 *
 * diary.csv wins over watched.csv where they disagree (PLAN §5): watched.csv
 * only has a log date, diary.csv additionally has rating/rewatch/the actual
 * watched date, so it's strictly richer whenever a film appears in both.
 * ratings.csv only fills in a rating that neither other file supplied.
 */

import type { ISODate } from "@schema/catalog.ts";
import { csvRecords } from "./csv.ts";
import { normalize } from "./match.ts";
import type { LetterboxdExportFiles } from "./unzip.ts";

export interface LetterboxdFilmRecord {
  title: string;
  year: number | null;
  /** Slug parsed from the row's "Letterboxd URI" column, e.g. "iron-man". */
  slug: string | null;
  watchedDate: ISODate;
  rating10: number | null;
  rewatch: boolean;
}

interface RawRow {
  title: string;
  year: number | null;
  slug: string | null;
  /** The CSV's own "Date" column. */
  date: ISODate | null;
  /** diary.csv's "Watched Date" column; `null` for watched.csv/ratings.csv rows. */
  watchedDate: ISODate | null;
  rating10: number | null;
  rewatch: boolean;
}

function slugFromUri(uri: string): string | null {
  const match = uri.match(/\/film\/([^/]+)\/?/);
  return match?.[1] ?? null;
}

function parseYear(value: string): number | null {
  const year = Number.parseInt(value, 10);
  return Number.isFinite(year) ? year : null;
}

/** Letterboxd star ratings are 0.5–5 in half-star steps; the app's internal scale is 1–10. */
function parseRating10(value: string): number | null {
  const stars = Number.parseFloat(value);
  return Number.isFinite(stars) && stars > 0 ? Math.round(stars * 2) : null;
}

function parseRawRows(text: string): RawRow[] {
  return csvRecords(text).map((record) => ({
    title: record.Name ?? "",
    year: record.Year ? parseYear(record.Year) : null,
    slug: record["Letterboxd URI"] ? slugFromUri(record["Letterboxd URI"]) : null,
    date: record.Date || null,
    watchedDate: record["Watched Date"] || null,
    rating10: record.Rating ? parseRating10(record.Rating) : null,
    rewatch: (record.Rewatch ?? "").trim().toLowerCase() === "yes",
  }));
}

function keyFor(row: Pick<RawRow, "slug" | "title" | "year">): string {
  return row.slug ?? `${normalize(row.title)}::${row.year ?? ""}`;
}

/**
 * Collapses every diary.csv row for one film (there can be several — each
 * rewatch gets its own row) into a single record: the earliest logged watch
 * is canonical, `rewatch` is true if there's more than one row or any row is
 * itself flagged as a rewatch, and the rating is the most recently-set one
 * (a rewatch commonly updates the rating).
 */
function resolveDiaryGroup(group: RawRow[]): LetterboxdFilmRecord | null {
  const dated = group
    .filter((r) => r.watchedDate ?? r.date)
    .sort((a, b) => (a.watchedDate ?? a.date ?? "").localeCompare(b.watchedDate ?? b.date ?? ""));
  const canonical = dated[0];
  if (!canonical) return null;

  const rated = [...dated].reverse().find((r) => r.rating10 !== null);

  return {
    title: canonical.title,
    year: canonical.year,
    slug: canonical.slug,
    watchedDate: canonical.watchedDate ?? canonical.date ?? "",
    rating10: rated?.rating10 ?? null,
    rewatch: group.length > 1 || group.some((r) => r.rewatch),
  };
}

/** Merges the up-to-three export files into one record per watched film. */
export function mergeLetterboxdExport(files: LetterboxdExportFiles): LetterboxdFilmRecord[] {
  const byKey = new Map<string, LetterboxdFilmRecord>();

  if (files.watched) {
    for (const raw of parseRawRows(files.watched)) {
      if (!raw.date) continue;
      byKey.set(keyFor(raw), {
        title: raw.title,
        year: raw.year,
        slug: raw.slug,
        watchedDate: raw.date,
        rating10: null,
        rewatch: false,
      });
    }
  }

  if (files.diary) {
    const groups = new Map<string, RawRow[]>();
    for (const raw of parseRawRows(files.diary)) {
      const key = keyFor(raw);
      const group = groups.get(key);
      if (group) group.push(raw);
      else groups.set(key, [raw]);
    }
    for (const [key, group] of groups) {
      const resolved = resolveDiaryGroup(group);
      if (!resolved) continue;
      const existingRating = byKey.get(key)?.rating10 ?? null;
      byKey.set(key, { ...resolved, rating10: resolved.rating10 ?? existingRating });
    }
  }

  if (files.ratings) {
    for (const raw of parseRawRows(files.ratings)) {
      const key = keyFor(raw);
      const existing = byKey.get(key);
      if (existing) {
        if (existing.rating10 === null) existing.rating10 = raw.rating10;
      } else if (raw.date) {
        byKey.set(key, {
          title: raw.title,
          year: raw.year,
          slug: raw.slug,
          watchedDate: raw.date,
          rating10: raw.rating10,
          rewatch: false,
        });
      }
    }
  }

  return [...byKey.values()];
}
